
import '@/components/article-editor/EditorNavbar/AiFeaturesDropdownButton/AiFeaturesDropdownButton.scss';
import { ArticleEditMode } from '@/@types/ArticleEditMode';

// Other
import { SmartContentType } from '@/@types/SmartContentType';

// Components
import SmartContentLoader from '@/components/article-editor/EditorNavbar/SmartContentLoader/SmartContentLoader.vue';
import EventBus from '@/EventBus';
import { ARTICLE_PLAYER_CHANGE_STATE } from '@/events';
import helper from '@/helper';

// Models
import { Article } from '@/models/article/Article';
import { Paragraph } from '@/models/article/Paragraph';
import { ArticlePlayerStates } from '@/models/ArticlePlayerStates';
import { Logger } from '@/other/Logger';
import { ARTICLE_EDIT, DRAFT_EDIT } from '@/routerNames';
import apiService from '@/services/ApiService';
import articleEditService from '@/services/article/ArticleEditService';

// Services
import aiGenerationPollingService from '@/services/AiGenerationPollingService';
import { AiGenerationType } from '@/services/api-providers/AiGenerationServiceProvider';
import mobileResponsiveService from '@/services/MobileResponsiveService';
import notificationService from '@/services/NotificationService';
import articlePlayerProvider from '@/services/ui-providers/ArticlePlayerProvider';
import store from '@/store';
import { commitSetArticleMode } from '@/store/commits/sharedCommits';

// Commits and dispatchers
import { dispatchEnableOcrMode, dispatchGetDraftsCount } from '@/store/dispatchers/uiDispatchers';
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';

const promptTypes = [
  { value: 'meeting-notes', label: 'Meeting Notes' },
  { value: 'user-manual', label: 'User Manual' },
  { value: 'policy', label: 'Policy' },
  { value: 'status-report', label: 'Status Report' },
  { value: 'technical-documentation', label: 'Technical Documentation' },
  { value: 'requirements-documentation', label: 'Requirements Documentation' },
];

const log = new Logger('AiFeaturesDropdownButton');

@Component({
  computed: {
    SmartContentType() {
      return SmartContentType;
    },
  },
  components: { SmartContentLoader },
})
export default class AiFeaturesDropdownButton extends Vue {
  @Prop({ required: false, default: false }) isMenuItem: boolean;
  @Prop({ required: false, default: false }) isArticleEditPage: boolean;
  @Prop({ required: false, default: false }) isAllowEditAndDeleteArticle: boolean;
  @Prop({ required: true, default: false }) article: Article;

  isSmartNotesOpen = false;
  notesPrompt = '';
  noteTypes = promptTypes;
  isAiParagraphsLoading = false;
  isAiSmartNotesLoading = false;

  aiParagraphsProgress = 0;
  aiSmartNotesProgress = 0;

  get articleEditor() {
    return articleEditService.articleEditor;
  }

  get isMobile() {
    return mobileResponsiveService.isMobileView;
  }

  handleClickRecognizeText() {
    dispatchEnableOcrMode();
  }

  handleSubmitGenerateNotes(e: HTMLFormElement) {
    e.preventDefault();
    return this.handleGenerateNotes();
  }

  async handleGenerateNotes(predefinedPrompt?: string) {
    this.aiSmartNotesProgress = 0;

    const articleId = this.article.id;

    await dispatchGetDraftsCount();

    this.isAiSmartNotesLoading = true;

    try {
      await aiGenerationPollingService.generateNotes(
        {
          articleId,
          predefinedPrompt,
          prompt: !predefinedPrompt ? this.notesPrompt : undefined,
          isConsiderArticle: true,
        },
        {
          onProgress: (progress) => {
            this.aiSmartNotesProgress = progress;
          },
          onCompleted: (result) => {
            const { textTabs, draftId } = result;

            if (articleId !== this.articleEditor.article.id || !textTabs) return;

            this.notesPrompt = '';

            if (!this.isArticleEditPage) {
              this.$router.push({ name: DRAFT_EDIT, params: { mode: ArticleEditMode.NOTES, id: draftId } });
            } else {
              commitSetArticleMode(ArticleEditMode.NOTES);
            }

            store.state.articleEditor.article.textTabs = textTabs;

            const textTabIndex = textTabs.length > 2 ? 0 : textTabs.length - 1;

            articleEditService.setActiveTextTab({
              article: this.article,
              textTabId: textTabs[textTabIndex].id,
            });
          },
          onError: (error) => {
            log.error(error);
            notificationService.error(error);
          },
        }
      );
    } catch (err: any) {
      log.error(JSON.stringify(err));
    } finally {
      this.aiSmartNotesProgress = 0;

      this.isAiSmartNotesLoading = false;
    }
  }

  async handleGenerateAiParagraphs() {
    this.aiParagraphsProgress = 0;

    const articleId = this.article.id;

    await dispatchGetDraftsCount();

    this.isAiParagraphsLoading = true;

    try {
      await aiGenerationPollingService.generateParagraphs(
        { articleId },
        {
          onProgress: (progress) => {
            EventBus.$emit('smart-paragraphs-status-update-sidebar', {
              isLoading: true,
              progress,
            });
            this.aiParagraphsProgress = progress;
          },
          onCompleted: (result) => {
            if (articleId !== this.articleEditor.article.id) return;

            const paragraphs = result.paragraphs;

            if (!paragraphs?.length) {
              notificationService.warning('We not able to generate paragraphs for this recording.');
              return;
            }

            const parsedParagraphs: Paragraph[] = [];

            for (const paragraph of paragraphs) {
              parsedParagraphs.push(Paragraph.parse(paragraph));
            }

            this.articleEditor.article.paragraphs = parsedParagraphs;

            switch (this.$route.name) {
              case DRAFT_EDIT: {
                this.$router.push({ name: DRAFT_EDIT, params: { mode: ArticleEditMode.VIDEO } });
                break;
              }
              case ARTICLE_EDIT: {
                this.$router.push({ name: ARTICLE_EDIT, params: { mode: ArticleEditMode.VIDEO } });
                break;
              }
            }

            commitSetArticleMode(ArticleEditMode.VIDEO);

            helper.observerNotify(this.articleEditor.article.paragraphs);
            window.setTimeout(() => {
              articlePlayerProvider.videoPlayer.updatePlayerRef();
              articlePlayerProvider.videoPlayer.update();
              EventBus.$emit(ARTICLE_PLAYER_CHANGE_STATE, ArticlePlayerStates.paused);
            }, 0);
          },
          onError: (error) => {
            log.error(error);
            notificationService.error(error);
          },
        }
      );
    } catch (err: any) {
      log.error(JSON.stringify(err));
    } finally {
      this.isAiParagraphsLoading = false;
      this.aiParagraphsProgress = 0;

      EventBus.$emit('smart-paragraphs-status-update-sidebar', {
        isLoading: false,
        progress: 0,
      });
    }
  }

  handleCancelAiGeneration(type: SmartContentType, emit = true) {
    switch (type) {
      case SmartContentType.notes:
        this.isAiSmartNotesLoading = false;

        aiGenerationPollingService.cancel(AiGenerationType.NOTES, this.article.id);
        break;
      case SmartContentType.paragraphs:
        this.isAiParagraphsLoading = false;

        if (emit) {
          EventBus.$emit('smart-paragraphs-status-update-sidebar', {
            isLoading: false,
            progress: 0,
            canceled: true,
          });
        }
        aiGenerationPollingService.cancel(AiGenerationType.PARAGRAPHS, this.article.id);
        break;
    }
  }

  private async checkArticleStatusAndReconnection() {
    try {
      const paragraphsRes = await apiService.aiGeneration.getGenerationStatus(
        AiGenerationType.PARAGRAPHS,
        this.article.id
      );

      const isParagraphsGenerating =
        paragraphsRes.data.status === 'pending' || paragraphsRes.data.status === 'processing';

      if (!this.isAiParagraphsLoading && isParagraphsGenerating) {
        this.handleGenerateAiParagraphs();
      }
    } catch {
      // No active generation found — ignore
    }
  }

  private async checkSmartNotesStatusAndReconnect() {
    try {
      const smartNotesRes = await apiService.aiGeneration.getGenerationStatus(AiGenerationType.NOTES, this.article.id);

      const isSmartNotesGenerating =
        smartNotesRes.data.status === 'pending' || smartNotesRes.data.status === 'processing';

      if (!this.isAiSmartNotesLoading && isSmartNotesGenerating) {
        this.handleGenerateNotes();
      }
    } catch {
      // No active generation found — ignore
    }
  }

  async onMainMenuOpen() {
    // Fix for transition element re-rendering
    if (this.isSmartNotesOpen) {
      this.isSmartNotesOpen = false;
      this.isSmartNotesOpen = true;
    }

    if (this.isArticleEditPage) {
      await this.checkArticleStatusAndReconnection();
    }

    if (this.isAllowEditAndDeleteArticle) {
      await this.checkSmartNotesStatusAndReconnect();
    }
  }

  created() {
    EventBus.$on(
      'smart-paragraphs-status-update-menu',
      (statusData: { isLoading: boolean; progress: number; canceled?: boolean }) => {
        if (statusData.canceled) return this.handleCancelAiGeneration(SmartContentType.paragraphs, false);
        this.aiParagraphsProgress = statusData.progress;
        this.isAiParagraphsLoading = statusData.isLoading;
      }
    );
  }
}
